/**
 * QuietPulse media behaviour.
 *
 * This asset is loaded only for the QuietPulse product. It keeps the main product-gallery
 * frame tied to the active item's natural ratio and limits lifestyle video playback to
 * cards that are actually visible.
 */
(function () {
  if (window.__quietPulseMedia) return;
  window.__quietPulseMedia = true;

  var GALLERY_CLASS = 'qp-adaptive-media';
  var resizeFrame = 0;
  var galleryObservers = new WeakMap();
  var visibleLifestyleVideos = new Set();

  function mediaRatio(slide) {
    if (!slide) return 0;

    var video = slide.querySelector('video');
    if (video && video.videoWidth > 0 && video.videoHeight > 0) {
      return video.videoWidth / video.videoHeight;
    }

    var image = slide.querySelector('img');
    if (image && image.naturalWidth > 0 && image.naturalHeight > 0) {
      return image.naturalWidth / image.naturalHeight;
    }

    return 0;
  }

  function currentSlide(gallery) {
    if (!gallery) return null;

    var selected = gallery.querySelector('slideshow-slide[aria-hidden="false"]');
    if (selected) return selected;

    var slides = gallery.querySelectorAll('slideshow-slide');
    var track = gallery.querySelector('slideshow-slides');
    if (!slides.length) return null;

    if (track && track.clientWidth) {
      var midpoint = track.scrollLeft + track.clientWidth / 2;
      for (var index = 0; index < slides.length; index += 1) {
        var slide = slides[index];
        if (slide.offsetLeft <= midpoint && midpoint < slide.offsetLeft + slide.offsetWidth) {
          return slide;
        }
      }
    }

    return slides[0];
  }

  function syncGalleryVideo(gallery, activeSlide) {
    var deferredMedia = gallery.querySelectorAll('deferred-media');
    for (var deferredIndex = 0; deferredIndex < deferredMedia.length; deferredIndex += 1) {
      var deferred = deferredMedia[deferredIndex];
      if (!activeSlide || !activeSlide.contains(deferred)) continue;

      /* Product videos are normally deferred behind a play button. Load the
         active QuietPulse video immediately so selecting its gallery thumbnail
         starts playback without requiring a second tap. */
      deferred.setAttribute('autoplay', '');
      if (!deferred.hasAttribute('data-media-loaded') && typeof deferred.loadContent === 'function') {
        deferred.loadContent(false);
      }
    }

    var videos = gallery.querySelectorAll('video');
    for (var index = 0; index < videos.length; index += 1) {
      var video = videos[index];
      if (!activeSlide || !activeSlide.contains(video)) {
        video.pause();
      } else {
        video.autoplay = true;
        video.muted = true;
        video.defaultMuted = true;
        video.loop = true;
        video.playsInline = true;
        video.controls = false;
        var playPromise = video.play();
        if (playPromise && typeof playPromise.catch === 'function') playPromise.catch(function () {});
      }
    }
  }

  function applyGalleryRatio(gallery, slide) {
    if (!gallery || !slide) return false;

    var track = gallery.querySelector('slideshow-slides');
    var ratio = mediaRatio(slide);
    if (!track || !ratio) return false;

    track.style.aspectRatio = ratio.toFixed(4);
    syncGalleryVideo(gallery, slide);
    return true;
  }

  function applyWhenReady(gallery, slide) {
    var applied = applyGalleryRatio(gallery, slide);
    var image = slide && slide.querySelector('img');
    var video = slide && slide.querySelector('video');

    if (image && !image.complete) {
      image.addEventListener('load', function () {
        applyGalleryRatio(gallery, slide);
      }, { once: true });
    }

    if (video && !video.videoWidth) {
      video.addEventListener('loadedmetadata', function () {
        applyGalleryRatio(gallery, slide);
      }, { once: true });
    }

    return applied;
  }

  function initGallery(gallery) {
    if (!gallery) return;
    gallery.classList.add(GALLERY_CLASS);

    if (!galleryObservers.has(gallery)) {
      var observer = new MutationObserver(function (mutations) {
        for (var index = 0; index < mutations.length; index += 1) {
          if (mutations[index].attributeName === 'aria-hidden') {
            applyWhenReady(gallery, currentSlide(gallery));
            break;
          }
        }
      });

      observer.observe(gallery, {
        attributes: true,
        attributeFilter: ['aria-hidden'],
        subtree: true
      });
      galleryObservers.set(gallery, observer);
    }

    applyWhenReady(gallery, currentSlide(gallery));
  }

  function initGalleries(root) {
    var scope = root && root.querySelectorAll ? root : document;
    var galleries = scope.querySelectorAll('media-gallery');
    for (var index = 0; index < galleries.length; index += 1) initGallery(galleries[index]);
  }

  function playLifestyleVideo(video) {
    video.muted = true;
    video.defaultMuted = true;
    video.loop = true;
    video.playsInline = true;
    video.controls = false;
    var playPromise = video.play();
    if (playPromise && typeof playPromise.catch === 'function') playPromise.catch(function () {});
  }

  function initLifestyleVideos(root) {
    var scope = root && root.querySelectorAll ? root : document;
    var videos = scope.querySelectorAll('.social-proof .qp-autoplay-video');
    if (!videos.length) return;

    if (!('IntersectionObserver' in window)) {
      for (var fallbackIndex = 0; fallbackIndex < videos.length; fallbackIndex += 1) {
        playLifestyleVideo(videos[fallbackIndex]);
      }
      return;
    }

    if (!window.__quietPulseVideoObserver) {
      window.__quietPulseVideoObserver = new IntersectionObserver(function (entries) {
        for (var entryIndex = 0; entryIndex < entries.length; entryIndex += 1) {
          var entry = entries[entryIndex];
          var video = entry.target;
          if (entry.isIntersecting && entry.intersectionRatio >= 0.35 && !document.hidden) {
            visibleLifestyleVideos.add(video);
            playLifestyleVideo(video);
          } else {
            visibleLifestyleVideos.delete(video);
            video.pause();
          }
        }
      }, { threshold: [0, 0.35, 0.75] });
    }

    for (var index = 0; index < videos.length; index += 1) {
      var video = videos[index];
      if (video.dataset.qpObserved === 'true') continue;
      video.dataset.qpObserved = 'true';
      video.muted = true;
      video.defaultMuted = true;
      video.loop = true;
      video.playsInline = true;
      video.controls = false;
      window.__quietPulseVideoObserver.observe(video);
    }
  }

  function init(root) {
    initGalleries(root);
    initLifestyleVideos(root);
  }

  document.addEventListener('slideshow:select', function (event) {
    var gallery = event.target && event.target.closest
      ? event.target.closest('media-gallery.' + GALLERY_CLASS)
      : null;
    if (!gallery) return;
    applyWhenReady(gallery, (event.detail && event.detail.slide) || currentSlide(gallery));
  });

  document.addEventListener('visibilitychange', function () {
    visibleLifestyleVideos.forEach(function (video) {
      if (document.hidden) video.pause();
      else playLifestyleVideo(video);
    });
  });

  window.addEventListener('resize', function () {
    if (resizeFrame) cancelAnimationFrame(resizeFrame);
    resizeFrame = requestAnimationFrame(function () {
      resizeFrame = 0;
      initGalleries(document);
    });
  }, { passive: true });

  window.addEventListener('orientationchange', function () {
    initGalleries(document);
  });

  document.addEventListener('shopify:section:load', function (event) {
    init(event.target || document);
  });

  function boot() {
    init(document);
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot, { once: true });
  else boot();
})();
